import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles.css';
import supabase from '../../utils/supabaseClient';

function AddProducto({ onClose }) {
  // Existing state
  const [form, setForm] = useState({
    codigo: '',
    nombre: '',
    detalle: '',
    sub_familia_id: '',
    precio1: '',
    precio2: '',
    precio3: '',
    descuento: '',
    iva_id: '',
    color_numeric_id: '', // Changed from color_id
    talla_id: '',
    bodega_id: 1, // Default to bodega 1 (Bodega 1 Taras)
    cantidad: 0   // Default quantity is 0
  });
  
  // Color management state
  const [colores, setColores] = useState([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newColorId, setNewColorId] = useState('');
  const [newColorCode, setNewColorCode] = useState('#000000');
  const [colorSearch, setColorSearch] = useState('');
  
  // Dropdown state
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const colorDropdownRef = useRef(null);
  
  // Rest of your existing state...
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 6;
  const [message, setMessage] = useState('');
  const [subfamilias, setSubfamilias] = useState([]);
  const [ivas, setIvas] = useState([]);
  const [tallas, setTallas] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [inventarioRows, setInventarioRows] = useState([
    { bodega_id: 1, cantidad: 0 } // Default row for Bodega 1
  ]);
  
  const [currentFolder, setCurrentFolder] = useState('highres');
  const [folderHistory, setFolderHistory] = useState(['highres']);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // New state variables for talla
  const [showTallaInput, setShowTallaInput] = useState(false);
  const [newTallaId, setNewTallaId] = useState('');

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (colorDropdownRef.current && !colorDropdownRef.current.contains(event.target)) {
        // Check if the clicked element is part of the color dropdown or search input
        const isColorDropdown = event.target.closest('.color-dropdown');
        const isColorSearchInput = event.target.classList.contains('color-search-input');
        
        if (!isColorDropdown && !isColorSearchInput) {
          setColorDropdownOpen(false);
        }
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch data including colors with RGB codes
  useEffect(() => {
    async function fetchData() {
      const { data: subfamiliasData } = await supabase.from('subfamilia').select('*');
      setSubfamilias(subfamiliasData || []);

      const { data: ivasData } = await supabase.from('iva').select('*');
      setIvas(ivasData || []);

      // Fetch colors with RGB codes
      const { data: coloresData } = await supabase.from('color').select('*');
      setColores(coloresData || []);

      const { data: tallasData } = await supabase.from('talla').select('*');
      setTallas(tallasData || []);
      
      // Add this to fetch bodegas data
      const { data: bodegasData } = await supabase.from('bodega').select('*');
      setBodegas(bodegasData || []);
    }

    fetchData();
  }, []);

  // Refresh all data from database
  const refreshAllData = async () => {
    setRefreshing(true);
    try {
      // Refresh main data (subfamilias, ivas, colores, tallas, bodegas)
      const { data: subfamiliasData } = await supabase.from('subfamilia').select('*');
      setSubfamilias(subfamiliasData || []);

      const { data: ivasData } = await supabase.from('iva').select('*');
      setIvas(ivasData || []);

      const { data: coloresData } = await supabase.from('color').select('*');
      setColores(coloresData || []);

      const { data: tallasData } = await supabase.from('talla').select('*');
      setTallas(tallasData || []);
      
      const { data: bodegasData } = await supabase.from('bodega').select('*');
      setBodegas(bodegasData || []);
      
      // Refresh available images by triggering re-fetch
      const currentFolderValue = currentFolder;
      setCurrentFolder('');
      setTimeout(() => {
        setCurrentFolder(currentFolderValue);
      }, 100);
      
      setMessage('Datos actualizados correctamente');
    } catch (error) {
      console.error('Error refreshing data:', error);
      setMessage('Error al actualizar los datos');
    } finally {
      setRefreshing(false);
    }
  };

  // Function to add a new color
  async function handleAddColor(e) {
    e.preventDefault();
    
    if (!newColorId.trim()) {
      setMessage('El nombre del color no puede estar vacío');
      return;
    }
    
    const colorExists = colores.some(color => color.name === newColorId.trim());
    if (colorExists) {
      setMessage('Este nombre de color ya existe');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('color')
        .insert([{ 
          name: newColorId.trim(), 
          rgb_code: newColorCode 
        }])
        .select('numeric_id, name, rgb_code');
      
      if (error) throw error;
      
      const newColor = data[0];
      setColores([...colores, newColor]);
      
      // Select the new color using its numeric_id
      setForm({...form, color_numeric_id: newColor.numeric_id});
      
      setNewColorId('');
      setNewColorCode('#000000');
      setShowColorPicker(false);
      setMessage('Color agregado correctamente');
      
    } catch (error) {
      console.error('Error adding color:', error);
      setMessage(`Error al agregar el color: ${error.message}`);
    }
  }

  // Get selected color object
  const selectedColor = colores.find(c => c.numeric_id === form.color_numeric_id);

  // Filter colors based on search
  const filteredColores = colores.filter(color => {
    if (!colorSearch.trim()) return true;
    const searchLower = colorSearch.toLowerCase();
    const colorName = color.name ? color.name.toLowerCase() : '';
    const colorRgb = color.rgb_code ? color.rgb_code.toLowerCase() : '';
    return colorName.includes(searchLower) || colorRgb.includes(searchLower);
  });

  // Handle color selection
  const handleColorSelect = (colorNumericId) => {
    setForm({...form, color_numeric_id: colorNumericId});
    setColorDropdownOpen(false);
    setColorSearch(''); // Clear search when color is selected
  };

  // Function to add a new talla
  async function handleAddTalla(e) {
    e.preventDefault();
    
    // Validate talla input
    if (!newTallaId.trim()) {
      setMessage('El ID de la talla no puede estar vacío');
      return;
    }
    
    // Check if talla ID already exists
    const tallaExists = tallas.some(talla => talla.id === newTallaId.trim());
    if (tallaExists) {
      setMessage('Esta talla ya existe');
      return;
    }
    
    try {
      // Insert new talla into the database
      const { data, error } = await supabase
        .from('talla')
        .insert([{ id: newTallaId.trim() }]);
      
      if (error) throw error;
      
      // Update local state
      const newTalla = { id: newTallaId.trim() };
      setTallas([...tallas, newTalla]);
      
      // Select the new talla in the form
      setForm({...form, talla_id: newTallaId.trim()});
      
      // Reset talla input state
      setNewTallaId('');
      setShowTallaInput(false);
      setMessage('Talla agregada correctamente');
      
    } catch (error) {
      console.error('Error adding talla:', error);
      setMessage(`Error al agregar la talla: ${error.message}`);
    }
  }

  // Add a function to handle inventory quantity changes
  const handleInventarioChange = (index, value) => {
    const newRows = [...inventarioRows];
    newRows[index].cantidad = parseInt(value) || 0;
    setInventarioRows(newRows);
  };

  // Add a function to add a new inventory row
  const addInventarioRow = () => {
    // Get bodega IDs that aren't already in the rows
    const usedBodegas = inventarioRows.map(row => row.bodega_id);
    const availableBodegas = bodegas
      .filter(bodega => !usedBodegas.includes(bodega.id))
      .map(bodega => bodega.id);
    
    if (availableBodegas.length === 0) {
      setMessage('Ya has agregado todas las bodegas disponibles');
      return;
    }
    
    setInventarioRows([
      ...inventarioRows, 
      { bodega_id: availableBodegas[0], cantidad: 0 }
    ]);
  };

  // Add a function to remove an inventory row
  const removeInventarioRow = (index) => {
    if (inventarioRows.length <= 1) {
      return; // Keep at least one row
    }
    const newRows = inventarioRows.filter((_, i) => i !== index);
    setInventarioRows(newRows);
  };

  // Existing functions...
  async function handleSave(e) {
    e.preventDefault();
    setMessage('');

    // Validate required fields
    if (!form.codigo || !form.nombre || !form.precio1 || !form.precio2 || !form.precio3 || !form.sub_familia_id) {
      setMessage('Por favor, complete todos los campos obligatorios.');
      return;
    }

    // Validate prices are greater than zero
    const precio1 = parseFloat(form.precio1);
    const precio2 = parseFloat(form.precio2);
    const precio3 = parseFloat(form.precio3);

    if (precio1 <= 0) {
      setMessage('El Precio 1 debe ser mayor que cero.');
      return;
    }

    if (precio2 <= 0) {
      setMessage('El Precio 2 debe ser mayor que cero.');
      return;
    }

    if (precio3 <= 0) {
      setMessage('El Precio 3 debe ser mayor que cero.');
      return;
    }

    // Prepare the data for insertion
    const productoData = {
      codigo: form.codigo,
      nombre: form.nombre,
      detalle: form.detalle || null,
      sub_familia_id: form.sub_familia_id ? parseInt(form.sub_familia_id, 10) : null,
      precio1: precio1,
      precio2: precio2,
      precio3: precio3,
      descuento: form.descuento ? parseFloat(form.descuento) : 0,
      iva_id: form.iva_id ? parseInt(form.iva_id, 10) : null,
      color_numeric_id: form.color_numeric_id || null, // Changed from color_id
      talla_id: form.talla_id || null
    };

    // Insert the product into the database
    const { data: producto, error: productoError } = await supabase.from('producto').insert([productoData]).select('id').single();
    if (productoError) {
      console.error('Error creating product:', productoError);
      setMessage('Error al agregar producto');
      return;
    }

    // Add inventory records for all rows with quantity > 0
    const inventarioPromises = inventarioRows
      .filter(row => row.cantidad > 0)
      .map(row => {
        const inventarioData = {
          producto_id: producto.id,
          bodega_id: row.bodega_id,
          cantidad: row.cantidad
        };
        return supabase.from('inventario').insert([inventarioData]);
      });

    if (inventarioPromises.length > 0) {
      const results = await Promise.all(inventarioPromises);
      const hasErrors = results.some(result => result.error);
      if (hasErrors) {
        console.error('Error adding some inventory records');
        setMessage('Producto creado pero hubo un error al agregar algunos registros de inventario');
      }
    }

    // Add selected images to the product
    for (const imageUrl of selectedImages) {
      const { error: imageError } = await supabase.from('images').insert([
        { url: imageUrl, producto_id: producto.id }
      ]);
      if (imageError) {
        setMessage('Producto e inventario creados, pero hubo un error al guardar algunas imágenes');
        return;
      }
    }

    setMessage('¡Producto agregado exitosamente!');
    setForm({
      codigo: '',
      nombre: '',
      detalle: '',
      sub_familia_id: '',
      precio1: '',
      precio2: '',
      precio3: '',
      descuento: '',
      iva_id: '',
      color_numeric_id: '', // Changed from color_id
      talla_id: '',
      bodega_id: 1,
      cantidad: 0
    });
    setInventarioRows([{ bodega_id: 1, cantidad: 0 }]);
    setSelectedImages([]);
    setTimeout(() => {
      if (onClose) onClose();
    }, 1000);
  }

  // Replace your fetchAvailableImages function with a memoized version:
  const fetchAvailableImages = useCallback(async () => {
    try {
      const { data: { publicUrl } } = supabase.storage
        .from('productsamples')
        .getPublicUrl('');
      
      const { data, error } = await supabase
        .storage
        .from('productsamples')
        .list(currentFolder, {
          sortBy: { column: 'name', order: 'asc' }
        });
      
      if (error) {
        console.error(`Error fetching from ${currentFolder}:`, error);
        return;
      }
      
      // Better folder detection
      const formattedItems = data.map(item => {
        const isFolder = item.metadata === null || 
                        item.id === undefined ||
                        !item.name.includes('.');
        
        if (isFolder) {
          return {
            name: item.name,
            path: `${currentFolder}/${item.name}`,
            isFolder: true
          };
        } else {
          const path = `${currentFolder}/${item.name}`;
          return {
            name: item.name,
            path: path,
            url: `${publicUrl}${path}`,
            isFolder: false
          };
        }
      });
      
      // Sort folders first, then files
      const sortedItems = formattedItems.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
      
      setAvailableImages(sortedItems);
      
    } catch (err) {
      console.error('Error in fetchAvailableImages:', err);
    }
  }, [currentFolder]); // Add currentFolder as dependency

  // Update your useEffect that calls fetchAvailableImages:
  useEffect(() => {
    fetchAvailableImages();
  }, [fetchAvailableImages]); // This properly includes the dependency

  // Calculate the current images to display for the current page
  // Only show valid image files (skip .placeholder and non-image files)
  const validImageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  const filteredImages = availableImages.filter(item => {
    if (!item.name) return false;
    if (item.isFolder) return item.name.toLowerCase().includes((search || '').toLowerCase());
    const lowerName = item.name.toLowerCase();
    if (lowerName === '.placeholder') return false;
    if (!validImageExtensions.some(ext => lowerName.endsWith(ext))) return false;
    return lowerName.includes((search || '').toLowerCase());
  });

  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);

  const imagesToDisplay = filteredImages.slice(
    (currentPage - 1) * imagesPerPage,
    currentPage * imagesPerPage
  );

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  // Toggle image selection
  function toggleImageSelection(url) {
    setSelectedImages(prev =>
      prev.includes(url) ? prev.filter(img => img !== url) : [...prev, url]
    );
  }

  const navigateToFolder = (folderPath) => {
    setFolderHistory(prev => [...prev, folderPath]);
    setCurrentFolder(folderPath);
    setCurrentPage(1); // Reset to first page when changing folders
  };

  const navigateBack = () => {
    if (folderHistory.length <= 1) return;
    
    const newHistory = [...folderHistory];
    newHistory.pop(); // Remove current folder
    const previousFolder = newHistory[newHistory.length - 1];
    
    setFolderHistory(newHistory);
    setCurrentFolder(previousFolder);
    setCurrentPage(1); // Reset to first page when going back
  };

  const createNewFolder = async () => {
    if (!newFolderName.trim()) {
      setMessage('El nombre de la carpeta no puede estar vacío');
      return;
    }
    
    try {
      // Create an empty file as a placeholder (Supabase doesn't have an explicit "create folder" API)
      const { error } = await supabase.storage
        .from('productsamples')
        .upload(`${currentFolder}/${newFolderName}/.placeholder`, new Blob(['']));
      
      if (error) {
        console.error('Error creating folder:', error);
        setMessage(`Error al crear carpeta: ${error.message}`);
        return;
      }
      
      // Update UI
      setMessage('Carpeta creada correctamente');
      setNewFolderName('');
      setShowNewFolderInput(false);
      
      // Refresh the folder contents
      fetchAvailableImages();
      
    } catch (err) {
      console.error('Error in createNewFolder:', err);
      setMessage(`Error: ${err.message}`);
    }
  };


  return (
    <div style={{
      maxWidth: 440,
      padding: 32,
      borderRadius: 12,
      background: '#fff',
      boxShadow: '0 4px 24px rgba(0,0,0,0.10)'
    }}>
      <div className="popup-header">
        <h2 style={{
          textAlign: 'center',
          marginBottom: 0,
          color: '#e60026',
          letterSpacing: 1,
          fontWeight: 700
        }}>
          CREAR NUEVO PRODUCTO
        </h2>
        <div className="header-actions">
          <button 
            type="button" 
            className="refresh-button" 
            onClick={refreshAllData}
            disabled={refreshing}
            title="Actualizar datos desde la base de datos"
          >
            {refreshing ? '🔄' : '↻'}
          </button>
          <button
            onClick={onClose}
            className="popup-close"
            title="Cerrar"
          >×</button>
        </div>
      </div>
      
      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={{ fontWeight: 500 }}>
          Código:
          <input
            value={form.codigo}
            onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Nombre:
          <input
            value={form.nombre}
            onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Detalle:
          <input
            value={form.detalle}
            onChange={e => setForm(f => ({ ...f, detalle: e.target.value }))}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Subfamilia:
          <select
            value={form.sub_familia_id}
            onChange={e => setForm(f => ({ ...f, sub_familia_id: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          >
            <option value="">Seleccione una subfamilia</option>
            {subfamilias.map(s => (
              <option key={s.id} value={String(s.id)}>{s.nombre}</option>
            ))}
          </select>
        </label>
        <label style={{ fontWeight: 500 }}>
          Precio 1:
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={form.precio1}
            onChange={e => setForm(f => ({ ...f, precio1: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Precio 2:
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={form.precio2}
            onChange={e => setForm(f => ({ ...f, precio2: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Precio 3:
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={form.precio3}
            onChange={e => setForm(f => ({ ...f, precio3: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          Descuento:
          <input
            type="number"
            step="0.01"
            value={form.descuento}
            onChange={e => setForm(f => ({ ...f, descuento: e.target.value }))}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          />
        </label>
        <label style={{ fontWeight: 500 }}>
          IVA:
          <select
            value={form.iva_id}
            onChange={e => setForm(f => ({ ...f, iva_id: e.target.value }))}
            style={{
              width: '100%',
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ccc',
              marginTop: 4
            }}
          >
            <option value="">Seleccione IVA</option>
            {ivas.map(i => (
              <option key={i.id} value={String(i.id)}>
                {i.porcentaje*100}%
              </option>
            ))}
          </select>
        </label>
        {/* Custom Color Dropdown */}
        <label style={{ fontWeight: 500 }}>
          Color:
          <div className="color-select-container" ref={colorDropdownRef}>
            <div 
              className="color-select-header"
              onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
            >
              {form.color_numeric_id ? (
                <>
                  <div 
                    className={`color-preview ${selectedColor?.name && selectedColor.name.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                    style={selectedColor?.name && selectedColor.name.toLowerCase() === 'colores' ? 
                      { display: 'block' } : 
                      { backgroundColor: selectedColor?.rgb_code || '#ccc', display: 'block' }
                    }
                  />
                  <div className="color-name">{selectedColor?.name}</div>
                </>
              ) : (
                <div className="color-name">Seleccione color</div>
              )}
            </div>
            
            {colorDropdownOpen && (
              <div className="color-dropdown">
                <div className="color-dropdown-header">Colores</div>
                
                {/* Color Search Bar */}
                <div className="color-search-container">
                  <input
                    type="text"
                    placeholder="Buscar colores..."
                    value={colorSearch}
                    onChange={(e) => setColorSearch(e.target.value)}
                    className="color-search-input"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </div>
                
                {filteredColores.length > 0 ? (
                  filteredColores.map(color => (
                    <div 
                      key={color.numeric_id}
                      className={`color-option ${form.color_numeric_id === color.numeric_id ? 'selected' : ''}`}
                      onClick={() => handleColorSelect(color.numeric_id)}
                      style={{ display: 'flex', width: '100%' }}
                    >
                      <div className="color-option-box">
                        <div 
                          className={`color-option-preview ${color.name && color.name.toLowerCase() === 'colores' ? 'rainbow-gradient' : ''}`}
                          style={color.name && color.name.toLowerCase() === 'colores' ? {} : { backgroundColor: color.rgb_code || '#ccc' }}
                        />
                      </div>
                      <div className="color-option-name">
                        {color.name}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-colors-message">
                    No se encontraron colores que coincidan con la búsqueda.
                  </div>
                )}
                
                <div 
                  className="add-color-option"
                  onClick={() => {
                    setColorDropdownOpen(false);
                    setShowColorPicker(true);
                  }}
                >
                  <span className="add-icon">+</span>
                  <span>Agregar nuevo color</span>
                </div>
              </div>
            )}
          </div>
        </label>
        
        {/* Color Picker Modal */}
        {showColorPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 8,
              width: '90%',
              maxWidth: 400,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Agregar Nuevo Color</h3>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Identificador del Color:
                <input
                  type="text"
                  value={newColorId}
                  onChange={e => setNewColorId(e.target.value)}
                  placeholder="Ej: ROJO, AZUL, VERDE"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                />
              </label>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Código RGB:
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                  <input
                    type="color"
                    value={newColorCode}
                    onChange={e => setNewColorCode(e.target.value)}
                    style={{
                      width: 40,
                      height: 40,
                      padding: 0,
                      border: 'none'
                    }}
                  />
                  <input
                    type="text"
                    value={newColorCode}
                    onChange={e => setNewColorCode(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 8,
                      borderRadius: 6,
                      border: '1px solid #ccc'
                    }}
                  />
                </div>
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowColorPicker(false);
                    setNewColorId('');
                    setNewColorCode('#000000');
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddColor}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e60026',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Agregar Color
                </button>
              </div>
            </div>
          </div>
        )}
        
        <label style={{ fontWeight: 500 }}>
          Talla:
          <div className="talla-select-container">
            <select
              value={form.talla_id}
              onChange={e => setForm(f => ({ ...f, talla_id: e.target.value }))}
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                border: '1px solid #ccc',
                marginTop: 4
              }}
            >
              <option value="">Seleccione talla</option>
              {tallas.map(t => (
                <option key={t.id} value={t.id}>{t.id}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowTallaInput(true)}
              style={{
                marginTop: 8,
                padding: '6px 10px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                fontSize: '13px'
              }}
            >
              <span style={{ marginRight: '4px', fontSize: '16px' }}>+</span> 
              Agregar nueva talla
            </button>
          </div>
        </label>
        
        {/* Talla Input Modal */}
        {showTallaInput && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: 20,
              borderRadius: 8,
              width: '90%',
              maxWidth: 400,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: 16 }}>Agregar Nueva Talla</h3>
              
              <label style={{ display: 'block', marginBottom: 12 }}>
                Identificador de la Talla:
                <input
                  type="text"
                  value={newTallaId}
                  onChange={e => setNewTallaId(e.target.value)}
                  placeholder="Ej: S, M, L, XL, 36, 38"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid #ccc',
                    marginTop: 4
                  }}
                />
              </label>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowTallaInput(false);
                    setNewTallaId('');
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: 6,
                    backgroundColor: '#f5f5f5',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddTalla}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e60026',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer'
                  }}
                >
                  Agregar Talla
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add this section before the submit button */}
        <div className="form-section">
          <h3 style={{ marginTop: '20px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
            Inventario
          </h3>
          
          {inventarioRows.map((row, index) => (
            <div 
              key={index} 
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}
            >
              <select
                value={row.bodega_id}
                onChange={(e) => {
                  const newRows = [...inventarioRows];
                  newRows[index].bodega_id = parseInt(e.target.value);
                  setInventarioRows(newRows);
                }}
                style={{ flex: '2', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                {bodegas.map(bodega => (
                  <option 
                    key={bodega.id} 
                    value={bodega.id}
                    disabled={inventarioRows.some(r => r !== row && r.bodega_id === bodega.id)}
                  >
                    {bodega.nombre}
                  </option>
                ))}
              </select>
              
              <input
                type="number"
                min="0"
                value={row.cantidad}
                onChange={(e) => handleInventarioChange(index, e.target.value)}
                style={{ flex: '1', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              
              <button
                type="button"
                onClick={() => removeInventarioRow(index)}
                disabled={inventarioRows.length <= 1}
                style={{
                  padding: '8px',
                  backgroundColor: inventarioRows.length <= 1 ? '#f0f0f0' : '#e60026',
                  color: inventarioRows.length <= 1 ? '#999' : 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: inventarioRows.length <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addInventarioRow}
            disabled={inventarioRows.length >= bodegas.length}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: inventarioRows.length >= bodegas.length ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px',
              opacity: inventarioRows.length >= bodegas.length ? 0.7 : 1
            }}
          >
            <span style={{ marginRight: '4px', fontSize: '16px' }}>+</span>
            Agregar bodega
          </button>
          
          {inventarioRows.length >= bodegas.length && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
              Ya has agregado todas las bodegas disponibles
            </div>
          )}
        </div>
        
        <button
          type="submit"
          className="secondary-button"
          style={{
            backgroundColor: '#e60026',
            color: 'white',
            padding: '12px',
            borderRadius: '6px',
            border: 'none',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Agregar
        </button>
        
        {message && (
          <div style={{
            marginTop: 12,
            color: message.includes('correctamente') ? 'green' : '#e60026',
            fontWeight: 600,
            textAlign: 'center'
          }}>
            {message}
          </div>
        )}
      </form>
      
      {/* Refresh Images Button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        <button
          type="button"
          onClick={fetchAvailableImages}
          style={{
            padding: '8px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '10px'
          }}
        >
          Refrescar Imágenes
        </button>
      </div>
      <div className="image-gallery" style={{ marginTop: 0 }}>
        <h3 style={{ textAlign: 'center', marginBottom: 16 }}>Imágenes disponibles</h3>
        
        {/* Add folder navigation */}
        <div className="folder-navigation">
          <button 
            type="button" 
            className="back-button" 
            onClick={navigateBack}
            disabled={folderHistory.length <= 1}
            style={{
              padding: '6px 10px',
              backgroundColor: folderHistory.length <= 1 ? '#f0f0f0' : '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: folderHistory.length <= 1 ? 'not-allowed' : 'pointer',
              marginRight: '10px',
              opacity: folderHistory.length <= 1 ? 0.5 : 1
            }}
          >
            ⬅️ Atrás
          </button>
          
          <span style={{
            flex: '1',
            fontFamily: 'monospace',
            padding: '6px 10px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {currentFolder}
          </span>
          
          <button 
            type="button" 
            onClick={() => setShowNewFolderInput(!showNewFolderInput)}
            style={{
              padding: '6px 10px',
              backgroundColor: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              cursor: 'pointer',
              marginLeft: '10px'
            }}
          >
            📁 Nueva Carpeta
          </button>
        </div>
        
        {/* New folder input */}
        {showNewFolderInput && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '12px',
            marginBottom: '12px'
          }}>
            <input
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              placeholder="Nombre de la carpeta"
              style={{
                flex: '1',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
            <button 
              onClick={createNewFolder}
              disabled={!newFolderName.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#e60026',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                opacity: newFolderName.trim() ? 1 : 0.7
              }}
            >
              Crear
            </button>
            <button 
              onClick={() => {
                setShowNewFolderInput(false);
                setNewFolderName('');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
          </div>
        )}
        
        {/* Update image grid to handle folders */}
        <div className="image-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
          justifyContent: 'center'
        }}>
          {imagesToDisplay && imagesToDisplay.length > 0 ? (
            imagesToDisplay.map((item, index) => (
              item && item.isFolder ? (
                // Render folder
                <div 
                  key={index}
                  onClick={() => navigateToFolder(item.path)}
                  style={{
                    height: '120px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
                  <span style={{ 
                    fontSize: '13px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%'
                  }}>
                    {item.name}
                  </span>
                </div>
              ) : (
                // Render image
                <div 
                  key={index}
                  className={`thumbnail-container ${selectedImages.includes(item.url) ? 'selected' : ''}`}
                  style={{
                    position: 'relative',
                    height: '120px',
                    border: `2px solid ${selectedImages.includes(item.url) ? '#e60026' : '#ddd'}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f8f8f8'
                  }}
                  onClick={() => toggleImageSelection(item.url)}
                >
                  <img 
                    src={item.url}
                    alt={`Imagen ${index + 1}`}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {selectedImages.includes(item.url) && (
                    <div style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#e60026',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      ✓
                    </div>
                  )}
                </div>
              )
            ))
          ) : (
            <div style={{ gridColumn: 'span 3', textAlign: 'center', padding: '20px' }}>
              No hay imágenes en esta carpeta
            </div>
          )}
        </div>

        {/* Keep your existing pagination */}
        <div className="pagination" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: 16,
          gap: 12
        }}>
          <button
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            className="pagination-btn"
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: '1px solid #ccc',
              backgroundColor: currentPage === 1 ? '#f0f0f0' : '#e60026',
              color: currentPage === 1 ? '#999' : '#fff',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Anterior
          </button>
          <span style={{ fontWeight: 600 }}>Página {currentPage} de {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            className="pagination-btn"
            
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddProducto;